import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Toast } from './types';

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

export const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation
    setIsVisible(true);
  }, []);

  const handleRemove = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className="w-7 h-7 text-green-600" />;
      case 'error':
        return <XCircleIcon className="w-7 h-7 text-red-600" />;
      case 'warning':
        return <ExclamationCircleIcon className="w-7 h-7 text-yellow-600" />;
      default:
        return <ExclamationCircleIcon className="w-7 h-7 text-blue-600" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 border-green-300 shadow-green-100';
      case 'error':
        return 'bg-red-50 border-red-300 shadow-red-100';
      case 'warning':
        return 'bg-yellow-50 border-yellow-300 shadow-yellow-100';
      default:
        return 'bg-blue-50 border-blue-300 shadow-blue-100';
    }
  };

  return (
    <div
      className={`w-full ${getBgColor()} border-2 rounded-xl p-5 shadow-lg transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          <h4 className="text-lg font-semibold text-gray-900 leading-tight">{toast.title}</h4>
          {toast.message && (
            <p className="mt-2 text-sm text-gray-700 leading-relaxed">{toast.message}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          <button
            className="p-1 rounded-full hover:bg-black hover:bg-opacity-5 transition-colors duration-200 text-gray-500 hover:text-gray-700"
            onClick={handleRemove}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
